/**
 * Complete Simplified Styled Select Box Component with Bundle Support
 * Converts native select elements into custom styled dropdowns
 * Simplified version with basic bundle support (no inventory fetching)
 */

console.log('🎨 Simplified StyledSelect.js SCRIPT STARTING TO LOAD...')
;(function () {
    'use strict'

    console.log('🎨 Simplified StyledSelect.js loaded successfully!')
    console.log('🎨 Simplified StyledSelect.js - Current URL:', window.location.href)
    console.log('🎨 Simplified StyledSelect.js - Document ready state:', document.readyState)

    class StyledSelect {
        constructor(selectElement, options = {}) {
            this.select = selectElement
            this.options = {
                placeholder: 'Choose an option',
                searchable: false,
                multiple: false,
                isSizeSelector: false,
                ...options,
            }

            this.isOpen = false
            this.selectedOption = null

            // Capture original option states before any processing
            this.originalOptionStates = this.captureOriginalOptionStates()

            this.init()
        }

        captureOriginalOptionStates() {
            const states = []
            Array.from(this.select.options).forEach((option, index) => {
                states[index] = {
                    value: option.value,
                    text: option.textContent?.trim(),
                    disabled: option.disabled,
                    selected: option.selected,
                    hasStockClass: option.classList?.contains
                        ? option.classList.contains('out-of-stock') ||
                        option.classList.contains('unavailable') ||
                        option.classList.contains('disabled')
                        : false,
                    // Check if option text indicates unavailability
                    isUnavailableText:
                        /unavailable|out of stock|sold out/i.test(
                            option.textContent || ''
                        ),
                }
            })
            console.log('🔍 StyledSelect: Captured original option states:', states)
            return states
        }

        init() {
            // Auto-select first available option if current selection is invalid
            // This must happen BEFORE creating the styled select
            this.setInitialSelection()

            // Create the styled select container
            this.createStyledSelect()

            // Add event listeners
            this.addEventListeners()

            // Set initial state
            this.updateDisplay()

            // Hide the original select
            this.select.style.display = 'none'
        }

        createStyledSelect() {
            // Create main container
            this.container = document.createElement('div')
            this.container.className = 'styled-select-container'
            this.container.style.cssText = `
                position: relative;
                width: 100%;
                color: #404040;
                font-family: "DMSans";
                font-size: 12.984px;
                font-style: normal;
                font-weight: 500;
                line-height: 159.5%;  
                letter-spacing: -0.26px;
                text-transform: uppercase;
            `

            // Create the select button
            this.selectButton = document.createElement('button')
            this.selectButton.type = 'button'
            this.selectButton.className = 'styled-select-button'
            this.selectButton.style.cssText = `
                width: 100%;
                min-width: 220px;
                padding: 0.75rem 1rem 0.75rem 2rem;
                border: 1px solid #F1F1F1;
                border-radius: 0.5rem;
                background: white;
                cursor: pointer;
                display: flex;
                align-items: center;
                justify-content: space-between;
                font-size: 0.875rem;
                transition: all 0.2s ease;
                outline: none;
                text-transform: uppercase;
                color: #404040;
                font-family: "DMSans";
                font-size: 12.984px;
                font-style: normal;
                font-weight: 500;
                line-height: 159.5%;
                letter-spacing: -0.26px;
            `

            // Create the display text span
            this.displayText = document.createElement('span')
            this.displayText.className = 'styled-select-display'
            this.selectButton.appendChild(this.displayText)

            // Create the arrow icon
            this.arrow = document.createElement('span')
            this.arrow.className = 'styled-select-arrow'
            this.arrow.innerHTML = `
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
                    <polyline points="6,9 12,15 18,9"></polyline>
                </svg>
            `
            this.arrow.style.cssText = `
                transition: transform 0.2s ease;
                color: #6b7280;
            `
            this.selectButton.appendChild(this.arrow)

            // Create the dropdown
            this.dropdown = document.createElement('div')
            this.dropdown.className = 'styled-select-dropdown'
            this.dropdown.style.cssText = `
                position: absolute;
                top: 100%;
                left: 0;
                right: 0;
                background: white;
                border: 1px solid #d1d5db;
                border-top: none;
                border-radius: 0 0 0.5rem 0.5rem;
                max-height: 200px;
                overflow-y: auto;
                z-index: 1000;
                display: none;
                box-shadow: 0 4px 6px -1px rgba(0, 0, 0, 0.1);
            `

            // Create options list
            this.optionsList = document.createElement('div')
            this.optionsList.className = 'styled-select-options'
            this.dropdown.appendChild(this.optionsList)

            // Add elements to container
            this.container.appendChild(this.selectButton)
            this.container.appendChild(this.dropdown)

            // Insert after the original select
            this.select.parentNode.insertBefore(this.container, this.select.nextSibling)

            // Populate options
            this.populateOptions()
        }

        populateOptions() {
            this.optionsList.innerHTML = ''

            // Check if we're on desktop (lg breakpoint and above)
            const isDesktop = window.innerWidth >= 1024

            // Collect all valid options first
            const validOptions = []
            Array.from(this.select.options).forEach((option) => {
                // For size selectors, include ALL options regardless of disabled state
                // For other selectors, skip disabled options
                const shouldSkip = !this.options.isSizeSelector && option.disabled && option.value !== ''

                // Also skip placeholder options (value is empty or text matches known placeholders)
                const isPlaceholder =
                    option.value === '' ||
                    /select an option|choose your size|please select/i.test(
                        option.textContent
                    )

                if (!shouldSkip && !isPlaceholder) {
                    validOptions.push({
                        value: option.value,
                        text: option.textContent,
                        disabled: false, // Force all size options to be enabled in the styled select
                        selected: option.selected,
                    })
                }
            })

            // Keep original order - no sorting

            // Create and append option elements
            validOptions.forEach((optionData) => {
                const optionElement = this.createOptionElement(optionData)
                this.optionsList.appendChild(optionElement)
            })
        }

        createOptionElement({ value, text, disabled = false, selected = false }) {
            const option = document.createElement('div')
            option.className = 'styled-select-option'
            option.dataset.value = value
            option.textContent = text

            option.style.cssText = `
                padding: 0.75rem 1rem;
                cursor: ${disabled ? 'not-allowed' : 'pointer'};
                opacity: ${disabled ? '0.5' : '1'};
                transition: background-color 0.2s ease;
                border-bottom: 1px solid #f3f4f6;
            `

            if (selected) {
                option.style.backgroundColor = '#000'
                option.style.color = '#fff'
            }

            if (!disabled) {
                option.addEventListener('click', () => this.selectOption(value))
                option.addEventListener('mouseenter', () => {
                    if (!selected) {
                        // option.style.backgroundColor = '#000'
                        // option.style.color = '#fff'
                    }
                })
                option.addEventListener('mouseleave', () => {
                    if (!selected) {
                        option.style.backgroundColor = 'transparent'
                    }
                })
            }

            return option
        }

        addEventListeners() {
            // Toggle dropdown on button click
            this.selectButton.addEventListener('click', (e) => {
                e.preventDefault()
                this.toggleDropdown()
            })

            // Close dropdown when clicking outside
            document.addEventListener('click', (e) => {
                if (!this.container.contains(e.target)) {
                    this.closeDropdown()
                }
            })

            // Handle keyboard navigation
            this.selectButton.addEventListener('keydown', (e) => {
                if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault()
                    this.toggleDropdown()
                }
            })

            // Listen for changes on the original select
            this.select.addEventListener('change', () => {
                this.updateDisplay()
                this.populateOptions()
            })
        }

        toggleDropdown() {
            if (this.isOpen) {
                this.closeDropdown()
            } else {
                this.openDropdown()
            }
        }

        openDropdown() {
            this.isOpen = true
            this.dropdown.style.display = 'block'
            this.arrow.style.transform = 'rotate(180deg)'
            this.selectButton.style.borderColor = '#00AEEF'
            this.selectButton.style.boxShadow = '0 0 0 3px rgba(0, 174, 239, 0.1)'
        }

        closeDropdown() {
            this.isOpen = false
            this.dropdown.style.display = 'none'
            this.arrow.style.transform = 'rotate(0deg)'
            this.selectButton.style.borderColor = '#d1d5db'
            this.selectButton.style.boxShadow = 'none'
        }

        selectOption(value) {
            // Update the original select
            this.select.value = value

            // Trigger change event on the original select
            const event = new Event('change', { bubbles: true })
            this.select.dispatchEvent(event)

            // For Shopify OptionSelectors, also trigger the individual option selector change
            if (this.select.classList.contains('single-option-selector')) {
                // Find the corresponding Shopify OptionSelector and update it
                const optionIndex =
                    this.select.getAttribute('data-index') ||
                    (this.select.id && this.select.id.match(/option(\d+)/)
                        ? this.select.id.match(/option(\d+)/)[1]
                        : null)

                if (optionIndex) {
                    // Trigger Shopify's variant selection logic
                    setTimeout(() => {
                        // Force a re-evaluation of the variant selection
                        const mainSelector =
                            document.querySelector('#product-selectors') ||
                            document.querySelector('#quickview-product-selectors')
                        if (mainSelector) {
                            const mainEvent = new Event('change', { bubbles: true })
                            mainSelector.dispatchEvent(mainEvent)
                        }
                    }, 50)
                }
            }

            // Update display
            this.updateDisplay()

            // Close dropdown
            this.closeDropdown()
        }

        updateDisplay() {
            const selectedOption = this.select.options[this.select.selectedIndex]
            if (selectedOption && selectedOption.value) {
                this.displayText.textContent = selectedOption.textContent
                this.displayText.style.color = '#111827'
            } else {
                this.displayText.textContent = this.options.placeholder
                this.displayText.style.color = '#9ca3af'
            }
        }

        setInitialSelection() {
            const currentOption = this.select.options[this.select.selectedIndex]
            const currentState = this.originalOptionStates[this.select.selectedIndex]

            console.log('🎯 StyledSelect: setInitialSelection - Current option:', {
                value: currentOption?.value,
                text: currentOption?.textContent,
                disabled: currentOption?.disabled,
                selectedIndex: this.select.selectedIndex,
                originalState: currentState,
            })

            // Check if current selection is invalid using original states
            const isInvalidSelection =
                !currentOption ||
                !currentOption.value ||
                currentOption.value === '' ||
                !currentState ||
                currentState.disabled ||
                currentState.hasStockClass ||
                currentState.isUnavailableText ||
                /select an option|choose your size|please select|choose size/i.test(
                    currentState.text || ''
                )

            console.log('🎯 StyledSelect: isInvalidSelection:', isInvalidSelection)

            if (isInvalidSelection) {
                console.log('🎯 StyledSelect: Original option states:', this.originalOptionStates)

                // Find the first available option using original states
                const firstAvailableOptionIndex = this.originalOptionStates.findIndex(
                    (state, index) => {
                        const isPlaceholder =
                            !state.value ||
                            state.value === '' ||
                            state.value === null ||
                            /select an option|choose your size|please select|choose size/i.test(
                                state.text || ''
                            )

                        const isUnavailable =
                            state.disabled || state.hasStockClass || state.isUnavailableText

                        const isAvailable = !isPlaceholder && !isUnavailable && state.value

                        console.log(`🎯 StyledSelect: Checking option ${index}:`, {
                            value: state.value,
                            text: state.text,
                            disabled: state.disabled,
                            hasStockClass: state.hasStockClass,
                            isUnavailableText: state.isUnavailableText,
                            isPlaceholder: isPlaceholder,
                            isUnavailable: isUnavailable,
                            isAvailable: isAvailable,
                        })

                        return isAvailable
                    }
                )

                console.log('🎯 StyledSelect: First available option index:', firstAvailableOptionIndex)

                if (firstAvailableOptionIndex >= 0) {
                    const firstAvailableOption = this.select.options[firstAvailableOptionIndex]
                    const firstAvailableState = this.originalOptionStates[firstAvailableOptionIndex]

                    console.log('🎯 StyledSelect: Selecting first available option:', {
                        index: firstAvailableOptionIndex,
                        value: firstAvailableOption.value,
                        text: firstAvailableOption.textContent,
                        originalState: firstAvailableState,
                    })

                    // Select the first available option
                    this.select.selectedIndex = firstAvailableOptionIndex
                    this.select.value = firstAvailableOption.value

                    // Trigger change event to ensure proper Shopify integration
                    const event = new Event('change', { bubbles: true })
                    this.select.dispatchEvent(event)
                } else {
                    console.log('🎯 StyledSelect: No available options found')
                }
            } else {
                console.log('🎯 StyledSelect: Current selection is valid, keeping it')
            }
        }

        // Public methods
        getValue() {
            return this.select.value
        }

        setValue(value) {
            this.select.value = value
            this.updateDisplay()
            this.populateOptions()
        }

        destroy() {
            if (this.container && this.container.parentNode) {
                this.container.parentNode.removeChild(this.container)
            }
            this.select.style.display = ''
        }

        // Method to refresh options when Shopify updates them
        refreshOptions() {
            this.populateOptions()
            this.updateDisplay()
        }
    }

    // Initialize styled selects for product pages
    function initProductStyledSelects() {
        // Find size select elements in multiple contexts:
        // 1. Regular product pages: .selector-wrapper.type-size select
        // 2. Bundle products: select elements with name containing "Size" or "size"
        const sizeSelects = document.querySelectorAll(
            '.selector-wrapper.type-size select, .selector-wrapper.type-size .single-option-selector, select[name*="Size"], select[name*="size"]'
        )

        sizeSelects.forEach((select) => {
            // Skip if already styled
            if (select.dataset.styledSelect) return

            // For regular product pages: check wrapper class
            const wrapper = select.closest('.selector-wrapper')
            if (wrapper && wrapper.classList.contains('type-size')) {
                // CRITICAL CHECK: Skip if this wrapper contains radio buttons
                // This prevents styling selects for profile and arch-support options
                if (wrapper.querySelector('.radio-selector')) {
                    return // Skip if this wrapper has radio buttons (non-size options)
                }

                // Additional check: make sure the select is not hidden
                if (
                    select.style.display === 'none' ||
                    wrapper.style.display === 'none'
                ) {
                    return // Skip hidden selects
                }
            }

            // For bundle products: check if the select name contains "Size" or "size"
            const selectName = select.getAttribute('name') || ''
            const isBundleSizeSelector = selectName.toLowerCase().includes('size')

            // Skip if it's not a size selector (either regular product or bundle)
            if (
                !(wrapper && wrapper.classList.contains('type-size')) &&
                !isBundleSizeSelector
            ) {
                return
            }

            // Mark as styled
            select.dataset.styledSelect = 'true'

            // Create styled select for size options only
            new StyledSelect(select, {
                placeholder: '',
                isSizeSelector: true,
            })
        })
    }

    // Initialize styled selects for quick view
    function initQuickViewStyledSelects() {
        console.log('🎨 StyledSelect: Starting quickview initialization')

        // Check if quickview modal exists and is open
        const quickviewModal = document.querySelector('#quickViewModal')
        if (!quickviewModal) {
            console.log('🎨 StyledSelect: Quickview modal not found')
            return
        }

        if (!quickviewModal.classList.contains('is-open')) {
            console.log('🎨 StyledSelect: Quickview modal not open')
            return
        }

        console.log('🎨 StyledSelect: Quickview modal found and is open')

        // Look for ALL selects in the quickview modal
        const allSelects = quickviewModal.querySelectorAll('select')
        console.log(`🎨 StyledSelect: Found ${allSelects.length} select elements in modal`)

        allSelects.forEach((select, index) => {
            console.log(`🎨 StyledSelect: Select ${index}:`, {
                id: select.id,
                name: select.name,
                className: select.className,
                value: select.value,
                optionsCount: select.options.length,
                styled: !!select.dataset.styledSelect,
                display: select.style.display,
                offsetParent: !!select.offsetParent,
                parentElement: select.parentElement?.tagName,
                parentClasses: select.parentElement?.className,
            })
        })

        // Look for size-related selects with multiple strategies
        const potentialSizeSelects = [
            // Strategy 1: Specific size selector IDs
            ...quickviewModal.querySelectorAll(
                '#qv-size-select, #size-select, [id*="size-select"]'
            ),
            // Strategy 2: Selects with size-related names
            ...quickviewModal.querySelectorAll(
                'select[name*="size"], select[name*="Size"]'
            ),
            // Strategy 3: Selects in wrapper with size-related classes
            ...quickviewModal.querySelectorAll(
                '.selector-wrapper.type-size select, .size-wrapper select, [class*="size"] select'
            ),
            // Strategy 4: Single option selectors (Shopify variant selectors)
            ...quickviewModal.querySelectorAll('.single-option-selector'),
        ]

        // Remove duplicates
        const uniqueSizeSelects = [...new Set(potentialSizeSelects)]

        console.log(`🎨 StyledSelect: Found ${uniqueSizeSelects.length} potential size selects`)

        if (uniqueSizeSelects.length === 0) {
            console.log('🎨 StyledSelect: No size selects found, trying broader search...')

            // Strategy 5: Look for any select that might be a variant selector
            const variantSelects = quickviewModal.querySelectorAll(
                'select:not(#quickview-product-selectors)'
            )
            console.log(
                `🎨 StyledSelect: Found ${variantSelects.length} variant selects (excluding main selector)`
            )

            variantSelects.forEach((select, index) => {
                console.log(`🎨 StyledSelect: Variant select ${index}:`, {
                    id: select.id,
                    name: select.name,
                    className: select.className,
                    options: Array.from(select.options).map((opt) => ({
                        value: opt.value,
                        text: opt.textContent,
                        selected: opt.selected,
                    })),
                })

                // Check if this looks like a size selector by examining options
                const optionTexts = Array.from(select.options).map((opt) =>
                    opt.textContent.toLowerCase()
                )
                const hasSizeOptions = optionTexts.some(
                    (text) =>
                        /^(xs|s|m|l|xl|xxl|\d+|small|medium|large)$/i.test(text.trim()) ||
                        /size|length|width/i.test(text)
                )

                if (hasSizeOptions) {
                    console.log(
                        `🎨 StyledSelect: Select ${index} appears to be a size selector based on options`
                    )
                    uniqueSizeSelects.push(select)
                }
            })
        }

        let successCount = 0
        uniqueSizeSelects.forEach((select, index) => {
            // Skip if already styled
            if (select.dataset.styledSelect) {
                console.log(`🎨 StyledSelect: Select ${index} already styled, skipping`)
                return
            }

            // Skip the hidden main selector
            if (select.id === 'quickview-product-selectors') {
                console.log(`🎨 StyledSelect: Skipping main selector ${select.id}`)
                return
            }

            // Check if the select is visible and has options
            if (select.options.length <= 1) {
                console.log(`🎨 StyledSelect: Select ${index} has no real options, skipping`)
                return
            }

            console.log(`🎨 StyledSelect: Attempting to style select ${index}:`, {
                id: select.id,
                name: select.name,
                className: select.className,
            })

            try {
                select.dataset.styledSelect = 'true'
                new StyledSelect(select, {
                    placeholder: '',
                    isSizeSelector: true,
                })
                successCount++
                console.log(`🎨 StyledSelect: Successfully styled select ${index}`)
            } catch (error) {
                console.error(`🎨 StyledSelect: Error styling select ${index}:`, error)
                delete select.dataset.styledSelect
            }
        })

        console.log(
            `🎨 StyledSelect: Quickview initialization complete. Styled ${successCount} selects.`
        )
    }

    // Initialize styled selects for upsell products
    function initUpsellStyledSelects() {
        const upsellSelects = document.querySelectorAll(
            '[id^="product-selectors-"], select[name*="Size"], select[name*="size"]'
        )

        upsellSelects.forEach((select) => {
            if (select.dataset.styledSelect) return

            // For regular upsell products: check if it's option3 (size)
            const optionNumber = select.getAttribute('data-option')
            const isRegularSizeSelector = optionNumber === 'option3'

            // For bundle products: check if the select name contains "Size" or "size"
            const selectName = select.getAttribute('name') || ''
            const isBundleSizeSelector = selectName.toLowerCase().includes('size')

            // Skip if it's not a size selector
            if (!isRegularSizeSelector && !isBundleSizeSelector) {
                return
            }

            // Check if this is inside a wrapper with radio buttons
            const wrapper = select.closest('.selector-wrapper')
            if (wrapper && wrapper.querySelector('.radio-selector')) {
                return // Skip if this wrapper has radio buttons
            }

            select.dataset.styledSelect = 'true'

            new StyledSelect(select, {
                placeholder: '',
                isSizeSelector: true,
            })
        })
    }

    // Simplified Bundle Styled Select initialization
    function initSimplifiedBundleStyledSelects() {
        console.log('🎨 Initializing Simplified Bundle Styled Selects...')

        // Find bundle size selects (much simpler targeting)
        const bundleSelects = document.querySelectorAll('.simple-bundle-options-radio select')

        bundleSelects.forEach(select => {
            if (select.dataset.styledSelect) return

            console.log('🎨 Creating Styled Select for bundle select:', select.id)

            select.dataset.styledSelect = 'true'
            new StyledSelect(select, {
                placeholder: 'Choose your size',
                isSizeSelector: true,
            })
        })
    }

    // Clean up any incorrectly applied styled selects
    function cleanupIncorrectStyledSelects() {
        // Find all styled select containers
        const styledContainers = document.querySelectorAll('.styled-select-container')

        styledContainers.forEach((container) => {
            const select = container.previousElementSibling
            if (select && select.tagName === 'SELECT') {
                const wrapper = select.closest('.selector-wrapper')
                const selectName = select.getAttribute('name') || ''
                const isBundleSizeSelector = selectName.toLowerCase().includes('size')

                // Remove styled select if:
                // 1. It's not a size selector (regular product), AND
                // 2. It's not a bundle size selector, AND
                // 3. The wrapper contains radio buttons (non-size options)
                if (
                    (!wrapper || !wrapper.classList.contains('type-size')) &&
                    !isBundleSizeSelector &&
                    wrapper &&
                    wrapper.querySelector('.radio-selector')
                ) {
                    // Remove the styled select container
                    if (container.parentNode) {
                        container.parentNode.removeChild(container)
                    }

                    // Reset the original select
                    if (select.dataset.styledSelect) {
                        delete select.dataset.styledSelect
                        select.style.display = ''
                    }
                }
            }
        })
    }

    // Updated initialization function for the simplified approach
    function initAllStyledSelectsSimplified() {
        // Clean up any incorrectly applied styled selects first
        cleanupIncorrectStyledSelects()

        // Initialize regular product styled selects
        initProductStyledSelects()
        initQuickViewStyledSelects()
        initUpsellStyledSelects()

        // Initialize simplified bundle styled selects
        if (document.body.classList.contains('template-productbundle-product-infinite')) {
            initSimplifiedBundleStyledSelects()
        }
    }

    // Initialize all styled selects (legacy function name for compatibility)
    function initAllStyledSelects() {
        initAllStyledSelectsSimplified()
    }

    // Simplified bundle system initialization
    function initSimplifiedBundleSystem() {
        console.log('🚀 Initializing Simplified Bundle System...')

        // Initialize bundle processing first
        if (typeof window.handleSimpleBundlesSimplified === 'function') {
            window.handleSimpleBundlesSimplified()
        }

        // Then initialize styled selects after bundle DOM is ready
        setTimeout(() => {
            initSimplifiedBundleStyledSelects()
        }, 1200) // Slightly longer delay to ensure bundle DOM is built

        // Also reinitialize when bundle changes
        document.addEventListener('bundleSelectionsChanged', () => {
            setTimeout(initSimplifiedBundleStyledSelects, 100)
        })
    }

    // Handle Shopify OptionSelector changes
    function handleShopifyOptionChanges() {
        // Listen for changes on Shopify's main product selector
        const mainSelectors = document.querySelectorAll(
            '#product-selectors, #quickview-product-selectors'
        )

        mainSelectors.forEach((mainSelector) => {
            mainSelector.addEventListener('change', () => {
                // Refresh all styled selects when the main variant changes
                const styledSelects = document.querySelectorAll('.styled-select-container')
                styledSelects.forEach((container) => {
                    if (container._styledSelectInstance) {
                        container._styledSelectInstance.refreshOptions()
                    }
                })
            })
        })
    }

    // Watch for new bundle selects (simplified)
    function watchForNewBundleSelects() {
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        // Look for new bundle selects
                        const newBundleSelects = node.classList?.contains('simple-bundle-options-radio')
                            ? node.querySelectorAll('select')
                            : node.querySelectorAll?.('.simple-bundle-options-radio select') || []

                        newBundleSelects.forEach(select => {
                            if (!select.dataset.styledSelect) {
                                console.log('🔄 Found new bundle select, styling it:', select.id)
                                select.dataset.styledSelect = 'true'
                                new StyledSelect(select, {
                                    placeholder: 'Choose your size',
                                    isSizeSelector: true,
                                })
                            }
                        })
                    }
                })
            })
        })

        observer.observe(document.body, {
            childList: true,
            subtree: true,
        })
    }

    // Watch for dynamically added selects
    function watchForNewSelects() {
        const observer = new MutationObserver((mutations) => {
            mutations.forEach((mutation) => {
                mutation.addedNodes.forEach((node) => {
                    if (node.nodeType === Node.ELEMENT_NODE) {
                        // Check if the added node is a select inside a size wrapper or bundle size selector
                        if (
                            node.tagName === 'SELECT' &&
                            !node.dataset.styledSelect &&
                            (node.closest('.selector-wrapper.type-size') ||
                                node.closest('.selector-wrapper[class*="type-size"]') ||
                                (node.getAttribute('name') &&
                                    node.getAttribute('name').toLowerCase().includes('size')))
                        ) {
                            const wrapper = node.closest('.selector-wrapper')

                            // Check if wrapper has type-size class (with or without hyphen)
                            const hasTypeSizeClass =
                                wrapper &&
                                (wrapper.classList.contains('type-size') ||
                                    wrapper.className.includes('type-size'))

                            if (hasTypeSizeClass) {
                                // CRITICAL CHECK: Skip if this wrapper contains radio buttons
                                if (wrapper.querySelector('.radio-selector')) {
                                    return // Skip if this wrapper has radio buttons (non-size options)
                                }

                                // Additional check: make sure the select is not hidden
                                if (
                                    node.style.display === 'none' ||
                                    wrapper.style.display === 'none'
                                ) {
                                    return // Skip hidden selects
                                }
                            }

                            // Check for bundle size selectors
                            const selectName = node.getAttribute('name') || ''
                            const isBundleSizeSelector = selectName.toLowerCase().includes('size')

                            // Only proceed if it's a size selector
                            if (hasTypeSizeClass || isBundleSizeSelector) {
                                node.dataset.styledSelect = 'true'
                                new StyledSelect(node, {
                                    placeholder: '',
                                    isSizeSelector: true,
                                })
                            }
                        }
                        // Check for selects within the added node
                        const selects = node.querySelectorAll
                            ? node.querySelectorAll(
                                '.selector-wrapper.type-size select, .selector-wrapper[class*="type-size"] select, .simple-bundle-options-radio select'
                            )
                            : []
                        selects.forEach((select) => {
                            if (!select.dataset.styledSelect) {
                                const wrapper = select.closest('.selector-wrapper')
                                const isBundleSelect = select.closest('.simple-bundle-options-radio')

                                if (wrapper) {
                                    // Check if wrapper has type-size class (with or without hyphen)
                                    const hasTypeSizeClass =
                                        wrapper.classList.contains('type-size') ||
                                        wrapper.className.includes('type-size')

                                    if (hasTypeSizeClass) {
                                        if (wrapper.querySelector('.radio-selector')) {
                                            return
                                        }
                                        if (
                                            select.style.display === 'none' ||
                                            wrapper.style.display === 'none'
                                        ) {
                                            return
                                        }
                                    }
                                }

                                select.dataset.styledSelect = 'true'
                                new StyledSelect(select, {
                                    placeholder: isBundleSelect ? 'Choose your size' : '',
                                    isSizeSelector: true,
                                })
                            }
                        })
                    }
                })
            })
        })
        observer.observe(document.body, {
            childList: true,
            subtree: true,
        })
    }

    // Initialize when DOM is ready
    function init() {
        // Wait for productScript to process selectors first
        setTimeout(() => {
            // Initial initialization
            initAllStyledSelectsSimplified()
        }, 500)

        // Watch for new selects
        watchForNewSelects()

        // Re-initialize after a longer delay to ensure productScript has finished
        setTimeout(initAllStyledSelectsSimplified, 2000)

        // Re-initialize when sliders are ready (for product pages)
        document.addEventListener('slidersReady', initAllStyledSelectsSimplified)

        console.log('🎨 StyledSelect: Setting up quickview event listeners...')

        // Re-initialize when quick view modal opens
        document.addEventListener('quickviewOpened', (event) => {
            console.log('🎨 StyledSelect: Quickview opened event received!', event.detail)
            console.log('🎨 StyledSelect: Quickview opened, initializing styled selects')
            // Run multiple times to catch any timing issues
            setTimeout(initQuickViewStyledSelects, 50)
            setTimeout(initQuickViewStyledSelects, 200)
            setTimeout(initQuickViewStyledSelects, 500)
            setTimeout(initQuickViewStyledSelects, 1000)

            // Also run immediately
            initQuickViewStyledSelects()
        })

        // Re-initialize when product data is fully loaded in quickview
        document.addEventListener('quickviewProductDataLoaded', (event) => {
            console.log('🎨 StyledSelect: Quickview product data loaded event received!', event.detail)
            console.log('🎨 StyledSelect: Quickview product data loaded, initializing styled selects')
            // Run immediately when product data is loaded
            initQuickViewStyledSelects()

            // Run again after a short delay to ensure DOM is ready
            setTimeout(initQuickViewStyledSelects, 50)

            // Run again after a longer delay to catch any late DOM changes
            setTimeout(initQuickViewStyledSelects, 200)

            // Run again after a longer delay to ensure everything is ready
            setTimeout(initQuickViewStyledSelects, 500)
        })

        // Also run periodically to catch any missed selects
        setInterval(() => {
            const quickviewModal = document.querySelector('#quickViewModal')
            if (quickviewModal && quickviewModal.classList.contains('is-open')) {
                // Specifically check for qv-size-select
                const qvSizeSelect = quickviewModal.querySelector('#qv-size-select')
                if (qvSizeSelect && !qvSizeSelect.dataset.styledSelect) {
                    console.log('🎨 StyledSelect: Found unstyled qv-size-select, re-initializing')
                    initQuickViewStyledSelects()
                }

                // Also check if the element exists but is not styled
                if (qvSizeSelect && !qvSizeSelect.dataset.styledSelect) {
                    console.log('🎨 StyledSelect: Periodic check - qv-size-select found but not styled')
                    console.log('🎨 StyledSelect: Element details:', {
                        id: qvSizeSelect.id,
                        name: qvSizeSelect.name,
                        className: qvSizeSelect.className,
                        display: qvSizeSelect.style.display,
                        offsetParent: !!qvSizeSelect.offsetParent,
                        computedStyle: window.getComputedStyle(qvSizeSelect).display,
                    })
                }
            }
        }, 1000)

        // Initialize bundle system if on bundle product page
        if (document.body.classList.contains('template-productbundle-product-infinite')) {
            initSimplifiedBundleSystem()
            watchForNewBundleSelects()
        }
    }

    // Initialize when DOM is ready
    if (document.readyState === 'loading') {
        document.addEventListener('DOMContentLoaded', init)
    } else {
        init()
    }

    // Auto-initialize for bundle products
    if (document.body.classList.contains('template-productbundle-product-infinite')) {
        document.addEventListener('DOMContentLoaded', () => {
            initSimplifiedBundleSystem()
            watchForNewBundleSelects()
        })

        // Also initialize if DOM is already loaded
        if (document.readyState !== 'loading') {
            initSimplifiedBundleSystem()
            watchForNewBundleSelects()
        }
    }

    // Expose functions globally
    window.StyledSelect = StyledSelect
    window.initProductStyledSelects = initProductStyledSelects
    window.initQuickViewStyledSelects = initQuickViewStyledSelects
    window.initUpsellStyledSelects = initUpsellStyledSelects
    window.initSimplifiedBundleStyledSelects = initSimplifiedBundleStyledSelects
    window.initAllStyledSelects = initAllStyledSelects
    window.initAllStyledSelectsSimplified = initAllStyledSelectsSimplified
    window.initSimplifiedBundleSystem = initSimplifiedBundleSystem
    window.cleanupIncorrectStyledSelects = cleanupIncorrectStyledSelects

    // Manual test function for debugging
    window.testQuickViewStyledSelect = function () {
        console.log('🧪 Testing QuickView styled select...')
        console.log('🧪 Running initQuickViewStyledSelects manually...')
        initQuickViewStyledSelects()
    }

    // Check if script is loading properly
    console.log('🎨 Simplified StyledSelect: Script loaded, all functions available:', {
        initQuickViewStyledSelects: typeof initQuickViewStyledSelects,
        initSimplifiedBundleStyledSelects: typeof initSimplifiedBundleStyledSelects,
        testQuickViewStyledSelect: typeof window.testQuickViewStyledSelect,
        StyledSelect: typeof StyledSelect,
    })

    // Monitor ALL custom events to see if quickview events are being dispatched
    const originalDispatchEvent = document.dispatchEvent
    document.dispatchEvent = function (event) {
        if (event.type.includes('quickview') || event.type.includes('Quick')) {
            console.log('🎨 Simplified StyledSelect: Custom event dispatched:', event.type, event.detail)
        }
        return originalDispatchEvent.call(this, event)
    }

    // Also add a simple manual test
    window.checkQuickViewModal = function () {
        console.log('🧪 Checking QuickView modal status...')
        const modal = document.querySelector('#quickViewModal')
        console.log('🧪 Modal found:', !!modal)
        if (modal) {
            console.log('🧪 Modal classes:', modal.className)
            console.log('🧪 Modal is open:', modal.classList.contains('is-open'))
            console.log('🧪 Modal style display:', modal.style.display)
            console.log('🧪 Modal computed display:', window.getComputedStyle(modal).display)

            const selects = modal.querySelectorAll('select')
            console.log('🧪 Selects in modal:', selects.length)
            selects.forEach((select, i) => {
                console.log(`🧪 Select ${i}:`, {
                    id: select.id,
                    name: select.name,
                    className: select.className,
                    options: select.options.length,
                })
            })
        }
    }

    // Test bundle system
    window.testBundleSystem = function() {
        console.log('🧪 Testing Bundle System...')
        console.log('🧪 Bundle Selects:', document.querySelectorAll('.simple-bundle-options-radio select'))
        console.log('🧪 Styled Bundle Selects:', document.querySelectorAll('.simple-bundle-options-radio .styled-select-container'))
    }

    console.log('🎨 Simplified Bundle StyledSelect loaded!')
})()